import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Box, Layers, TrendingUp, RotateCcw, ZoomIn, ZoomOut, Move } from 'lucide-react';

// Color palette for soil layers
const SOIL_COLORS = [
  0x8B4513, // Saddle brown
  0xA0522D, // Sienna
  0xCD853F, // Peru
  0xD2691E, // Chocolate
  0xDEB887, // Burlywood
  0xF4A460, // Sandy brown
];

// Get FOS-based color
const getFOSColor3D = (minFS) => {
  if (minFS === null || minFS === undefined) return 0x9CA3AF;
  if (minFS >= 1.5) return 0x22C55E;
  if (minFS >= 1.2) return 0xF59E0B;
  return 0xEF4444;
};

// Three.js Scene Component
const ThreeScene = ({ viewMode, piles, activePileId, onSelectPile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef({ isDragging: false, isRotating: false, lastX: 0, lastY: 0 });
  const animationRef = useRef(null);

  const activePile = piles.find(p => p.id === activePileId) || piles[0];

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Copy ref to local variable for cleanup function
    const container = containerRef.current;

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1e293b);
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    camera.position.set(15, 15, 15);
    camera.lookAt(0, -5, 0);
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 10, 5);
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-10, 10, -5);
    scene.add(directionalLight2);

    // Add grid helper
    const gridHelper = new THREE.GridHelper(20, 20, 0x4B5563, 0x374151);
    scene.add(gridHelper);

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!container) return;
      const width = container.clientWidth;
      const height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Use the renderer reference we already have (not containerRef.current)
      if (rendererRef.current && container) {
        container.removeChild(rendererRef.current.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Mouse controls for rotation/pan/zoom
  useEffect(() => {
    if (!containerRef.current || !cameraRef.current) return;

    const container = containerRef.current;
    const camera = cameraRef.current;
    const controls = controlsRef.current;

    const handleMouseDown = (e) => {
      if (e.button === 0) {
        controls.isRotating = true;
      } else if (e.button === 2) {
        controls.isDragging = true;
      }
      controls.lastX = e.clientX;
      controls.lastY = e.clientY;
    };

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - controls.lastX;
      const deltaY = e.clientY - controls.lastY;

      if (controls.isRotating) {
        // Orbit camera around center
        const spherical = new THREE.Spherical();
        const target = new THREE.Vector3(0, -5, 0);
        const offset = camera.position.clone().sub(target);
        
        spherical.setFromVector3(offset);
        spherical.theta -= deltaX * 0.01;
        spherical.phi -= deltaY * 0.01;
        spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
        
        offset.setFromSpherical(spherical);
        camera.position.copy(target).add(offset);
        camera.lookAt(target);
      }

      if (controls.isDragging) {
        // Pan camera
        const right = new THREE.Vector3();
        const up = new THREE.Vector3(0, 1, 0);
        camera.getWorldDirection(right);
        right.cross(up).normalize();
        
        camera.position.addScaledVector(right, -deltaX * 0.05);
        camera.position.y += deltaY * 0.05;
      }

      controls.lastX = e.clientX;
      controls.lastY = e.clientY;
    };

    const handleMouseUp = () => {
      controls.isRotating = false;
      controls.isDragging = false;
    };

    const handleWheel = (e) => {
      e.preventDefault();
      const target = new THREE.Vector3(0, -5, 0);
      const direction = camera.position.clone().sub(target).normalize();
      const distance = camera.position.distanceTo(target);
      const newDistance = Math.max(5, Math.min(50, distance + e.deltaY * 0.05));
      camera.position.copy(target).addScaledVector(direction, newDistance);
    };

    const handleContextMenu = (e) => e.preventDefault();

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('contextmenu', handleContextMenu);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Update scene based on view mode
  useEffect(() => {
    if (!sceneRef.current || !cameraRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;

    // Clear existing objects (except lights and grid)
    const objectsToRemove = [];
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        objectsToRemove.push(child);
      }
    });
    objectsToRemove.forEach((obj) => {
      scene.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });

    if (viewMode === 'pile' && activePile) {
      // Pile Cross Section View
      const scale = 0.3;
      const section = activePile.pileInputs.pileSections[0] || { diameter: 762, wallThickness: 25, length: 23.5 };
      const diameter = (section.diameter / 1000) * scale;
      const length = section.length * scale;
      const wallThickness = (section.wallThickness / 1000) * scale;

      // Soil layers
      activePile.soilLayers.forEach((layer, index) => {
        const layerHeight = (layer.to - layer.from) * scale;
        const yPos = -((layer.from + (layer.to - layer.from) / 2) * scale);
        const layerWidth = diameter * 4;

        const geometry = new THREE.BoxGeometry(layerWidth, layerHeight, layerWidth);
        const material = new THREE.MeshStandardMaterial({
          color: SOIL_COLORS[index % SOIL_COLORS.length],
          transparent: true,
          opacity: 0.6
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(0, yPos, 0);
        scene.add(mesh);
      });

      // Pile outer shell
      const pileGeometry = new THREE.CylinderGeometry(diameter / 2, diameter / 2, length, 32);
      const pileMaterial = new THREE.MeshStandardMaterial({
        color: 0x4B5563,
        metalness: 0.8,
        roughness: 0.2
      });
      const pileMesh = new THREE.Mesh(pileGeometry, pileMaterial);
      pileMesh.position.set(0, -length / 2, 0);
      scene.add(pileMesh);

      // Grout (inner)
      const groutGeometry = new THREE.CylinderGeometry(
        (diameter / 2 - wallThickness) * 0.95,
        (diameter / 2 - wallThickness) * 0.95,
        length + 0.05,
        32
      );
      const groutMaterial = new THREE.MeshStandardMaterial({ color: 0x9CA3AF });
      const groutMesh = new THREE.Mesh(groutGeometry, groutMaterial);
      groutMesh.position.set(0, -length / 2, 0);
      scene.add(groutMesh);

      // Ground plane
      const groundGeometry = new THREE.RingGeometry(diameter * 1.5, diameter * 2, 32);
      const groundMaterial = new THREE.MeshStandardMaterial({ color: 0x15803D, side: THREE.DoubleSide });
      const groundMesh = new THREE.Mesh(groundGeometry, groundMaterial);
      groundMesh.rotation.x = -Math.PI / 2;
      groundMesh.position.y = 0.05;
      scene.add(groundMesh);

      camera.position.set(5, 3, 5);
      camera.lookAt(0, -length / 2, 0);

    } else if (viewMode === 'plan') {
      // 3D Plan View
      const scale = 0.01;

      piles.forEach((pile) => {
        const x = (pile.position.x - 150) * scale;
        const z = (pile.position.y - 100) * scale;
        const section = pile.pileInputs.pileSections[0] || { diameter: 762, length: 23.5 };
        const diameter = section.diameter / 1000;
        const length = section.length;
        const isActive = pile.id === activePileId;
        const color = getFOSColor3D(pile.fos?.minFS);

        // Pile cylinder
        const pileGeometry = new THREE.CylinderGeometry(diameter / 2, diameter / 2, length, 16);
        const pileMaterial = new THREE.MeshStandardMaterial({
          color: color,
          metalness: 0.5,
          roughness: 0.3,
          emissive: isActive ? color : 0x000000,
          emissiveIntensity: isActive ? 0.3 : 0
        });
        const pileMesh = new THREE.Mesh(pileGeometry, pileMaterial);
        pileMesh.position.set(x, -length / 2, z);
        pileMesh.userData = { pileId: pile.id };
        scene.add(pileMesh);

        // Pile cap
        const capGeometry = new THREE.CylinderGeometry(diameter / 2 + 0.1, diameter / 2 + 0.1, 0.3, 16);
        const capMaterial = new THREE.MeshStandardMaterial({ color: isActive ? 0x3B82F6 : 0x6B7280 });
        const capMesh = new THREE.Mesh(capGeometry, capMaterial);
        capMesh.position.set(x, 0.2, z);
        scene.add(capMesh);
      });

      camera.position.set(8, 20, 8);
      camera.lookAt(0, -5, 0);

    } else if (viewMode === 'curve' && activePile?.results) {
      // Load-Settlement Curve in 3D
      const data = activePile.results.loadSettlementData;
      const ultimateCapacity = activePile.results.ultimateCapacity;
      const allowableCapacity = activePile.results.allowableCapacity;

      // Create line points
      const points = data.map((point) => {
        const x = (point.settlement / data[data.length - 1].settlement) * 5 - 2.5;
        const y = (point.load / ultimateCapacity) * 4;
        return new THREE.Vector3(x, y, 0);
      });

      // Main curve
      const curveGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const curveMaterial = new THREE.LineBasicMaterial({ color: 0x059669, linewidth: 3 });
      const curveLine = new THREE.Line(curveGeometry, curveMaterial);
      scene.add(curveLine);

      // Ultimate capacity line
      const ultimateLine = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-2.5, 4, 0),
        new THREE.Vector3(2.5, 4, 0)
      ]);
      const ultimateMaterial = new THREE.LineDashedMaterial({ color: 0xF97316, dashSize: 0.2, gapSize: 0.1 });
      const ultimateLineMesh = new THREE.Line(ultimateLine, ultimateMaterial);
      ultimateLineMesh.computeLineDistances();
      scene.add(ultimateLineMesh);

      // Allowable capacity line
      const allowableY = (allowableCapacity / ultimateCapacity) * 4;
      const allowableLine = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-2.5, allowableY, 0),
        new THREE.Vector3(2.5, allowableY, 0)
      ]);
      const allowableMaterial = new THREE.LineDashedMaterial({ color: 0x22C55E, dashSize: 0.2, gapSize: 0.1 });
      const allowableLineMesh = new THREE.Line(allowableLine, allowableMaterial);
      allowableLineMesh.computeLineDistances();
      scene.add(allowableLineMesh);

      // Axes
      const axisX = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-2.5, 0, 0),
        new THREE.Vector3(2.5, 0, 0)
      ]);
      const axisY = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-2.5, 0, 0),
        new THREE.Vector3(-2.5, 4.5, 0)
      ]);
      const axisMaterial = new THREE.LineBasicMaterial({ color: 0x9CA3AF });
      scene.add(new THREE.Line(axisX, axisMaterial));
      scene.add(new THREE.Line(axisY, axisMaterial));

      camera.position.set(0, 2, 8);
      camera.lookAt(0, 2, 0);
    }
  }, [viewMode, activePile, activePileId, piles]);

  const resetCamera = useCallback(() => {
    if (!cameraRef.current) return;
    const camera = cameraRef.current;
    camera.position.set(15, 15, 15);
    camera.lookAt(0, -5, 0);
  }, []);

  return (
    <div className="relative w-full h-[500px]">
      <div ref={containerRef} className="w-full h-full" />
      <div className="absolute top-4 right-4 flex gap-2">
        <Button variant="secondary" size="icon" onClick={resetCamera} title="Reset View">
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Main 3D Visualization Tab Component
export const Visualization3DTab = ({ piles, activePileId, onSelectPile }) => {
  const [activeView, setActiveView] = useState('pile');
  
  const activePile = piles.find(p => p.id === activePileId) || piles[0];
  
  return (
    <div className="space-y-6" data-testid="visualization-3d-tab">
      {/* View Selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList>
            <TabsTrigger value="pile" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Pile Cross-Section
            </TabsTrigger>
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <Box className="h-4 w-4" />
              3D Plan View
            </TabsTrigger>
            <TabsTrigger value="curve" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Load-Settlement 3D
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {activePile?.name || 'No pile selected'}
          </Badge>
        </div>
      </div>
      
      {/* 3D Canvas */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <ThreeScene 
            viewMode={activeView}
            piles={piles}
            activePileId={activePileId}
            onSelectPile={onSelectPile}
          />
        </CardContent>
      </Card>
      
      {/* Legend / Info Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Controls</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1">
            <p><strong>Rotate:</strong> Left-click + drag</p>
            <p><strong>Pan:</strong> Right-click + drag</p>
            <p><strong>Zoom:</strong> Scroll wheel</p>
          </CardContent>
        </Card>
        
        {activeView === 'pile' && activePile && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Pile Info: {activePile.name}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <p>Diameter: <span className="font-mono">{activePile.pileInputs.pileSections[0]?.diameter || 762} mm</span></p>
              <p>Length: <span className="font-mono">{activePile.pileInputs.pileSections[0]?.length || 23.5} m</span></p>
              <p>Soil Layers: <span className="font-mono">{activePile.soilLayers.length}</span></p>
            </CardContent>
          </Card>
        )}
        
        {activeView === 'plan' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Color Legend</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>FS ≥ 1.5 (Pass)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>1.2 ≤ FS &lt; 1.5 (Warning)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>FS &lt; 1.2 (Fail)</span>
              </div>
            </CardContent>
          </Card>
        )}
        
        {activeView === 'curve' && activePile?.results && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Capacity</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <p>Ultimate: <span className="font-mono text-orange-500">{activePile.results.ultimateCapacity.toFixed(3)} MN</span></p>
              <p>Allowable: <span className="font-mono text-green-500">{activePile.results.allowableCapacity.toFixed(3)} MN</span></p>
              <p>Governing: <span className="font-mono">{activePile.results.governingComponent}</span></p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Visualization3DTab;
